"use client";

import { useCallback, useMemo, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { useStore, useUserVip } from "@/stores/appStore";
import { uuid } from "@/lib/uuid";
import { useUploadConfig } from "./useUploadConfig";
import { useUploadSession } from "./useUploadSession";
import { validateFiles, validateCountBeforeUpload } from "../core/upload-validator";
import { UploadItem, UploadErrorCode } from "../core/types";
import { getFileInfo } from "@/stores/slices/lib/paperUtils";
import type { FileInfo } from "@/stores/slices/lib/paperTypes";
import { getUploadMessages } from "../locales/messages";

/**
 * onBeforeUpload 返回值类型
 * - false: 取消上传
 * - 对象: 继续上传，对象中的属性会传递给 uploadPapers
 */
export type OnBeforeUploadResult = null | {
  ignoreAiCol: boolean;
  // 未来可以扩展其他参数
  [key: string]: any;
};
export type OnBeforeUpload = (files: FileList | File[]) => Promise<OnBeforeUploadResult | false>;

interface UseUploadOptions {
  route?: string;
  subjectId?: string;
  uploadedCount?: number;
  onBeforeUpload?: OnBeforeUpload;
  onUploaded?: () => void;
}

/**
 * 文献上传主流程 Hook。
 *
 * 生命周期说明（每次调用 handleUpload 都视为「一轮上传」）：
 * 1. 启动阶段（Before Upload）
 *    - 通过 getAllUploadTasks 检查上一轮是否完全结束（所有任务为 success/error）
 *    - 如果上一轮已经全部结束，则 clearAllUploadSessions()，确保面板从空状态开始新一轮
 *
 * 2. 验证阶段（Validate）
 *    - 调用 validateFiles：拆分为 validFiles / invalidFiles
    - 对 invalidFiles 逐个创建 UploadItem(status: "error") 写入 uploadSessionSlice，
      这些任务不会进入 OSS 上传，但会在 UploadProgressPanel 中展示错误状态
 *
 * 3. 上传阶段（UploadPapers）
 *    - 为每个 validFile 预先计算 hash(FileInfo.id)，创建 UploadItem(status: "validating")
    - 将这些任务加入当前会话（addUploadTasks），随后统一设置为 status:"uploading"
    - 调用 paperSlice.uploadPapers(files, { taskIds, callbacks })，仅它负责与后端交互
    - 在回调中，根据 result.code → UploadErrorCode，统一写入 uploadSessionSlice
      （例如：-3→DUPLICATE, -1→SIZE_EXCEEDED, 5001→BALANCE_INSUFFICIENT, 其它→SERVER_ERROR）
      并把 progress / paper 信息同步到对应 UploadItem
 *
 * 4. 会话管理
 *    - useUploadSession 维护当前路由下的 sessionId 和本轮任务列表
    - 当当前 session 内任务全部结束时，可以通过 refreshSession 创建新的会话，
      避免后续追加上传时复用旧的任务列表
 *
 * 所有 UI（包括 UploadProgressPanel）只订阅 uploadSessionSlice.getAllUploadTasks()，
 * useUpload 不直接操作任何面板状态，只负责「把生命周期事件翻译成 UploadItem 的状态流」。
 */
export function useUpload(options: UseUploadOptions = {}) {
  const config = useUploadConfig();
  const uploadMessages = useMemo(() => getUploadMessages(), []);
  const { vip } = useUserVip();
  const uploadPapers = useStore((state) => state.uploadPapers);
  const clearAllUploadSessions = useStore((state) => state.clearAllUploadSessions);
  const getAllUploadTasks = useStore((state) => state.getAllUploadTasks);
  const openPaymentRecharge = useStore((state) => state.payment.openPaymentRecharge);
  const {
    sessionId,
    tasks,
    addUploadTasks,
    updateUploadTask,
    refreshSession,
  } = useUploadSession(options.route, options.subjectId);

  // 记录每个任务进度达到100的时间，用于超时检测
  const progress100TimersRef = useRef<Map<string, number>>(new Map());

  const handleUpload = useCallback(async (files: File[], source: UploadItem["source"] = "local") => {
    if (!files.length) {
      return;
    }

    let currentSessionId = sessionId;
    const ensureSessionId = () => {
      if (currentSessionId) {
        return currentSessionId;
      }
      const refreshedId = refreshSession(options.route, options.subjectId);
      if (!refreshedId) {
        console.error("ensureSessionId: refreshSession returned empty sessionId");
        return null;
      }
      currentSessionId = refreshedId;
      return currentSessionId;
    };

    // 在进入本次上传前，先查看"上一轮全局任务"是否已经全部结束
    // 如果全部结束（包括 success / warning / error），则清空所有会话，
    // 确保新一轮上传（无论是 PDF 还是非 PDF）都在一个全新的、干净的会话中进行
    const previousGlobalTasks = getAllUploadTasks();
    const previousAllCompleted = previousGlobalTasks.every(
      (task) => task.status === "success" || task.status === "warning" || task.status === "error"
    );
    if (previousGlobalTasks.length > 0 && previousAllCompleted) {
      clearAllUploadSessions();
      currentSessionId = "";
    }

    // ===== 阶段 2：总数校验阶段（Count Validation） =====
    // 在 onBeforeUpload 之前执行总数校验，如果总数超限，直接弹出提示并返回
    // 此时尚未创建任何 UploadItem，因此不会触发面板显示
    const countValidation = validateCountBeforeUpload(
      files.length,
      config,
      options.uploadedCount ?? 0
    );
    if (!countValidation.valid) {
      // 总数超限，直接 toast 提示并返回，不进入后续流程
      toast.error(countValidation.error.message);
      return;
    }

    // ===== 阶段 3：前置确认阶段（Pre-Upload Confirmation） =====
    // 在验证之前执行 onBeforeUpload，确保用户确认后再进行后续操作
    // 此时尚未创建任何 UploadItem，因此不会触发面板显示
    let uploadOptions: { ignoreAiCol?: boolean } = {};
    if (options.onBeforeUpload) {
      const result = await options.onBeforeUpload(files);
      if (result === false) {
        // 用户取消上传，直接返回，不进行任何后续操作
        toast.success(uploadMessages.errors.cancelled);
        return;
      }
      // result 是对象或null，表示继续上传，提取参数
      if (result === null || (result && typeof result === 'object')) {
        uploadOptions = { ...result };
      }
    }

    // ===== 阶段 4：验证阶段（Validation） =====
    // 在用户确认后，执行文件验证，此时会创建 UploadItem，面板开始显示
    const { validFiles, invalidFiles } = await validateFiles(files, {
      config,
      uploadedCount: options.uploadedCount ?? 0,
    });

    // 为验证失败的文件创建错误任务，添加到 session（此时面板会显示）
    if (invalidFiles.length) {
      const ensuredSessionId = ensureSessionId();
      if (!ensuredSessionId) {
        console.error("addUploadTasks for invalidFiles: sessionId is empty");
        toast.error(uploadMessages.errors.sessionNotReady);
        return;
      }
      const now = Date.now();
      const errorTasks: UploadItem[] = invalidFiles.map(({ file, error }) => ({
        id: uuid(),
        file,
        status: "error",
        progress: 0,
        source,
        startTime: now,
        endTime: now,
        error,
      }));
      addUploadTasks(ensuredSessionId, errorTasks);
    }

    // 如果没有有效文件，直接返回（但 invalidFiles 已经添加到面板）
    if (!validFiles.length) {
      return;
    }

    const now = Date.now();
    const fileInfoMap = new Map<File, FileInfo>();
    await Promise.all(
      validFiles.map(async (file) => {
        const fileInfo = await getFileInfo(file);
        fileInfoMap.set(file, fileInfo);
      })
    );

    const newTasks: UploadItem[] = validFiles.map((file) => {
      const fileInfo = fileInfoMap.get(file)!;
      return {
        id: uuid(),
        file,
        status: "validating",
        progress: 0,
        source,
        startTime: now,
        hash: fileInfo.id,
      };
    });

    const ensuredSessionId = ensureSessionId();
    if (!ensuredSessionId) {
      console.error("addUploadTasks for validFiles: sessionId is empty");
      toast.error(uploadMessages.errors.sessionNotReady);
      return;
    }

    const filesToUpload = [...validFiles];
    const filesInfoToUpload: FileInfo[] = filesToUpload.map(
      (file) => fileInfoMap.get(file)!
    );

    addUploadTasks(ensuredSessionId, newTasks);

    if (!filesToUpload.length) {
      options.onUploaded?.();
      return;
    }

    const tasksToUpload = newTasks;
    tasksToUpload.forEach((task) => {
      updateUploadTask(ensuredSessionId, task.id, {
        status: "uploading",
        progress: 0,
      });
    });

    const getResultError = (code: number) => {
      const maxSize = config?.maxFileSize ?? 0;
      switch (code) {
        case -3:
          return {
            code: UploadErrorCode.DUPLICATE,
            message: uploadMessages.errors.duplicate,
            retryable: false,
          };
        case -1:
          return {
            code: UploadErrorCode.SIZE_EXCEEDED,
            message: uploadMessages.errors.sizeExceeded(maxSize),
            retryable: false,
          };
        case 5001:
          return {
            code: UploadErrorCode.BALANCE_INSUFFICIENT,
            message: uploadMessages.errors.balanceInsufficient,
            retryable: false,
          };
        case -2:
          return {
            code: UploadErrorCode.SERVER_ERROR,
            message: uploadMessages.errors.uploadFailed,
            retryable: true,
          };
        default:
          return {
            code: UploadErrorCode.SERVER_ERROR,
            message: uploadMessages.errors.uploadFailed,
            retryable: true,
          };
      }
    };

    // 固定回调使用的 sessionId，避免异步回调执行时 session 已被清空或切换导致更新失败
    const uploadSessionId = ensuredSessionId;
    try {
      await uploadPapers({
        files: filesToUpload,
        subjectId: options.subjectId,
        ...uploadOptions,
        filesInfo: filesInfoToUpload,
        taskIds: tasksToUpload.map((task) => task.id),
        callbacks: {
          onProgress: (taskId, progress) => {
            const partial: Partial<UploadItem> = { progress };
            if (progress < 100) {
              partial.status = "uploading";
              // 清除之前的100进度计时器
              progress100TimersRef.current.delete(taskId);
            } else if (progress === 100) {
              // 当进度达到 100 时，记录时间，用于超时检测
              progress100TimersRef.current.set(taskId, Date.now());
            }
            updateUploadTask(uploadSessionId, taskId, partial);
          },
          onTaskComplete: (taskId, result) => {
            const endTime = Date.now();
            // 清除进度100的计时器，因为任务已完成
            progress100TimersRef.current.delete(taskId);

            if (result.code === 0) {
              updateUploadTask(uploadSessionId, taskId, {
                status: "success",
                progress: 100,
                endTime,
                error: undefined,
                paper: result.paper as any,
              });
              return;
            }

            if (result.code === -3) {
              const duplicateError = getResultError(-3);
              updateUploadTask(uploadSessionId, taskId, {
                status: "warning",  // 改为 warning
                progress: 100,
                endTime,
                paper: result.paper as any,
                error: {
                  ...duplicateError,
                },
              });
              return;
            }

            if (result.code === -1) {
              const sizeExceededError = getResultError(-1);
              updateUploadTask(uploadSessionId, taskId, {
                status: "error",
                progress: 100,
                endTime,
                error: {
                  ...sizeExceededError,
                },
              });
              return;
            }

            if (result.code === 30009) {
              const isVip = vip === 1;
              const message = isVip
                ? "文献上传已达上限，需清理后重新上传"
                : "文献上传已达上限，升级会员可上传2000篇";
              updateUploadTask(uploadSessionId, taskId, {
                status: "error",
                progress: 100,
                endTime,
                error: {
                  code: UploadErrorCode.UPLOAD_LIMIT_REACHED,
                  message,
                  retryable: false,
                },
              });
              return;
            }

            const fallbackError = getResultError(result.code);
            updateUploadTask(uploadSessionId, taskId, {
              status: "error",
              progress: 100,
              endTime,
              error: {
                ...fallbackError,
              },
            });
          },
          onAllTasksComplete: ({ uploadLimitReached }) => {
            if (uploadLimitReached && vip !== 1) {
              openPaymentRecharge(true);
              toast("文献上传已达上限，升级会员可上传2000篇");
            }
          },
        },
      });
      options.onUploaded?.();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(uploadMessages.errors.uploadFailed);
      tasksToUpload.forEach((task) => {
        updateUploadTask(uploadSessionId, task.id, {
          status: "error",
          error: {
            code: UploadErrorCode.SERVER_ERROR,
            message: uploadMessages.errors.uploadFailed,
            retryable: true,
          },
        });
      });
    }
  }, [config, options, sessionId, vip, addUploadTasks, clearAllUploadSessions, getAllUploadTasks, refreshSession, updateUploadTask, uploadPapers, uploadMessages, openPaymentRecharge]);

  // 超时检测：检查是否有任务进度达到100但状态仍然是uploading，超过一定时间后自动标记为错误
  useEffect(() => {
    const checkTimeout = () => {
      const now = Date.now();
      const TIMEOUT_MS = 90000; // 90秒超时（进度100后90秒仍未完成，视为失败）
      const store = useStore.getState();

      progress100TimersRef.current.forEach((timestamp, taskId) => {
        if (now - timestamp > TIMEOUT_MS) {
          // 查找对应的任务和sessionId
          const allSessions = store.uploadSessions;
          let foundSessionId: string | null = null;

          for (const [sessionId, session] of Object.entries(allSessions)) {
            const task = session.tasks.find(t => t.id === taskId);
            if (task && task.status === "uploading" && task.progress === 100) {
              foundSessionId = sessionId;
              break;
            }
          }

          // 如果找到超时的任务，标记为错误
          if (foundSessionId) {
            console.warn(`Task ${taskId} timed out after reaching 100% progress`);
            store.updateUploadTask(foundSessionId, taskId, {
              status: "error",
              progress: 100,
              endTime: now,
              error: {
                code: UploadErrorCode.SERVER_ERROR,
                message: uploadMessages.errors.uploadFailed,
                retryable: true,
              },
            });
            progress100TimersRef.current.delete(taskId);
          } else {
            // 任务不存在或已完成，清除计时器
            progress100TimersRef.current.delete(taskId);
          }
        }
      });
    };

    const interval = setInterval(checkTimeout, 5000); // 每5秒检查一次
    return () => clearInterval(interval);
  }, [getAllUploadTasks, uploadMessages]);

  return {
    config,
    sessionId,
    handleUpload,
  };
}
